import React, { useState, useRef, useEffect } from 'react';
import { X, AlertTriangle, Shield, Brain, Server, Clock, TrendingUp } from 'lucide-react';

interface SearchBarProps {
  onSearch: (query: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ onSearch }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>(['aws', 'credential', 'okta', 'ransomware']);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = () => {
    if (query.trim()) {
      if (!recentSearches.includes(query.trim())) {
        setRecentSearches(prev => [query.trim(), ...prev.slice(0, 4)]);
      }
      onSearch(query.trim());
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleRecentClick = (search: string) => {
    setQuery(search);
    onSearch(search);
    setIsOpen(false);
  };

  const handleQuickFilter = (filter: string) => {
    setQuery(filter);
    onSearch(filter);
    setIsOpen(false);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'threat': return <AlertTriangle className="w-4 h-4 text-red-400" />;
      case 'remediation': return <Shield className="w-4 h-4 text-green-400" />;
      case 'insight': return <Brain className="w-4 h-4 text-purple-400" />;
      case 'system': return <Server className="w-4 h-4 text-blue-400" />;
      default: return null;
    }
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-xl">
      {/* Search Input */}
      <div className="relative">
        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base">🔍</span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search threats, remediations, insights... (Press Enter)"
          className="w-full bg-arbitium-darker/80 border border-arbitium-primary/20 rounded-lg pl-10 pr-10 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-arbitium-accent/50 focus:ring-1 focus:ring-arbitium-accent/50 transition-all"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-arbitium-dark border border-arbitium-primary/20 rounded-xl shadow-2xl overflow-hidden z-50">
          <div className="p-4">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
              <Clock className="w-3 h-3" />
              Recent Searches
            </div>
            <div className="flex flex-wrap gap-2">
              {recentSearches.map((search, idx) => (
                <button
                  key={idx}
                  onClick={() => handleRecentClick(search)}
                  className="px-3 py-1.5 text-sm bg-arbitium-primary/10 text-gray-300 rounded-lg hover:bg-arbitium-primary/20 hover:text-white transition-colors"
                >
                  {search}
                </button>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-arbitium-primary/10">
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                <TrendingUp className="w-3 h-3" />
                Quick Filters
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'Threats', filter: 'threat', icon: 'threat' },
                  { label: 'Remediations', filter: 'remediation', icon: 'remediation' },
                  { label: 'Insights', filter: 'insight', icon: 'insight' },
                  { label: 'System', filter: 'system', icon: 'system' },
                ].map((item) => (
                  <button
                    key={item.filter}
                    onClick={() => handleQuickFilter(item.filter)}
                    className="flex items-center justify-center gap-2 px-3 py-2 text-xs bg-arbitium-darker rounded-lg text-gray-400 hover:text-white hover:bg-arbitium-primary/20 transition-colors"
                  >
                    {getCategoryIcon(item.icon)}
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-arbitium-primary/10 text-xs text-gray-600 text-center">
              Press <kbd className="px-1.5 py-0.5 bg-arbitium-darker rounded">Enter</kbd> to search
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
